import { useCallback, useEffect, useState } from 'react';
import {
  collection,
  deleteField,
  doc,
  getDocs,
  onSnapshot,
  orderBy,
  query,
  serverTimestamp,
  setDoc,
  updateDoc,
  where,
} from 'firebase/firestore';
import { ref as storageRef, uploadBytes, getDownloadURL } from 'firebase/storage';
import { useCollection } from 'react-firebase-hooks/firestore';
import { db, auth, storage } from '@/lib/firebase/config';
import { COLLECTIONS, splitPath, storagePath } from '@elka/firebase';
import type { PurchaseRequest, PurchaseRequestItem, PurchaseRequestStatus } from '@elka/types';

export type PurchaseRequestConversion =
  | { convertedBidId: string; convertedBidNumber: string }
  | { convertedOrderId: string; convertedOrderNumber: string };

export interface CreatePurchaseRequestInput {
  projectId: string;
  projectName: string;
  clientId?: string;
  deliveryAddress: string;
  dueDate: string; // YYYY-MM-DD
  items: PurchaseRequestItem[];
  notes?: string;
  photos: File[];
}

function purchaseRequestsCollection() {
  return collection(db, ...splitPath(COLLECTIONS.team.purchaseRequests));
}

export function usePurchaseRequests() {
  const [isUploading, setIsUploading] = useState(false);

  const requestsQuery = query(purchaseRequestsCollection(), orderBy('createdAt', 'desc'));
  const [snapshot, loading, queryError] = useCollection(requestsQuery);

  const requests: PurchaseRequest[] =
    snapshot?.docs.map((d) => ({ id: d.id, ...d.data() }) as PurchaseRequest) ?? [];

  const updateStatus = useCallback(async (id: string, status: PurchaseRequestStatus) => {
    await updateDoc(doc(purchaseRequestsCollection(), id), {
      status,
      // closedAt drives the "ימים עד סגירה" badge on the closed tab — set it the
      // moment the request closes, and clear it if the status moves back open.
      closedAt: status === 'closed' ? serverTimestamp() : deleteField(),
      updatedAt: serverTimestamp(),
    });
  }, []);

  const markConverted = useCallback(async (id: string, conversion: PurchaseRequestConversion) => {
    // Converting to an order closes the request — the material has been ordered.
    // Converting to a bid is only an intermediate step; the request stays in progress
    // until that bid later becomes an order (see closePurchaseRequestsForBid).
    const status: PurchaseRequestStatus =
      'convertedOrderId' in conversion ? 'closed' : 'in_progress';
    await updateDoc(doc(purchaseRequestsCollection(), id), {
      ...conversion,
      status,
      ...(status === 'closed' && { closedAt: serverTimestamp() }),
      updatedAt: serverTimestamp(),
    });
  }, []);

  // Office-created request — same document shape the team app writes, with the
  // back-office user standing in as the requester.
  const createRequest = useCallback(async (input: CreatePurchaseRequestInput) => {
    const user = auth.currentUser;
    if (!user) throw new Error('User not authenticated');
    setIsUploading(true);

    try {
      const docRef = doc(purchaseRequestsCollection());

      const photoUrls: string[] = [];
      for (const file of input.photos) {
        const ext = file.name.split('.').pop() ?? 'jpg';
        const filename = `${Date.now()}_${Math.random().toString(36).slice(2)}.${ext}`;
        const fileRef = storageRef(
          storage,
          storagePath('team', 'purchase-requests', docRef.id, filename)
        );
        await uploadBytes(fileRef, file, { contentType: file.type });
        photoUrls.push(await getDownloadURL(fileRef));
      }

      const notes = input.notes?.trim();
      await setDoc(docRef, {
        projectId: input.projectId,
        projectName: input.projectName,
        ...(input.clientId && { clientId: input.clientId }),
        deliveryAddress: input.deliveryAddress,
        dueDate: input.dueDate,
        items: input.items,
        ...(notes && { notes }),
        photoUrls,
        status: 'open' satisfies PurchaseRequestStatus,
        // Office-created request — no crew, the office user stands in as the reporter.
        reportedByWorkerName: user.displayName || user.email || 'משרד',
        reportedByWorkerAuthUid: user.uid,
        workerIds: [],
        workerNames: [],
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      });

      return docRef.id;
    } finally {
      setIsUploading(false);
    }
  }, []);

  return {
    requests,
    isLoading: loading,
    isUploading,
    error: queryError?.message ?? null,
    updateStatus,
    markConverted,
    createRequest,
  };
}

/**
 * Indirect close path: a purchase request was first converted to a supplier bid,
 * and that bid was later converted to an order. Find every purchase request that
 * points at this bid and close it, recording the order it ultimately produced.
 * Never throws — a failure here must not block the order-creation flow.
 */
export async function closePurchaseRequestsForBid(
  bidId: string,
  order: { id: string; orderNumber: string }
): Promise<void> {
  try {
    const snap = await getDocs(
      query(purchaseRequestsCollection(), where('convertedBidId', '==', bidId))
    );
    await Promise.all(
      snap.docs.map((d) =>
        updateDoc(d.ref, {
          status: 'closed' satisfies PurchaseRequestStatus,
          convertedOrderId: order.id,
          convertedOrderNumber: order.orderNumber,
          closedAt: serverTimestamp(),
          updatedAt: serverTimestamp(),
        })
      )
    );
  } catch (err) {
    console.error('Failed to close purchase requests for bid:', bidId, err);
  }
}

/** Live count of open purchase requests (sidebar badge). */
export function useOpenPurchaseRequestCount(): number {
  const [count, setCount] = useState(0);

  useEffect(() => {
    const openQuery = query(purchaseRequestsCollection(), where('status', '==', 'open'));
    return onSnapshot(
      openQuery,
      (snap) => setCount(snap.size),
      (err) => console.error('Failed to load open purchase requests count:', err)
    );
  }, []);

  return count;
}
